import { useEffect, useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import styled from "styled-components";

import { Topics } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { Dropdown, DropdownButton, SimpleDropdownItem } from "@similarweb/ui-components";
import { Box, Flex } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import { useLLMTabs } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/hooks/useLLMTabs";
import { ALL_TOPICS_NAME } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/LLMConsts";
import { navigateToReport } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/LLMTrackerUtils";
import type { IAiTrackerCampaign, LLModel } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/types";
import { allTrackers } from "app/services/track/track";

const TruncatedText = styled.span`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
`;

interface ITopicsFilterProps {
    selectedCampaign: IAiTrackerCampaign;
    isDisabled: boolean;
}

export const TopicsFilter = ({ selectedCampaign, isDisabled }: ITopicsFilterProps) => {
    const {
        params: { topic: queryTopic, model: queryModel },
    } = useCurrentStateAndParams();

    const { currentTab } = useLLMTabs();
    const [selectedTopic, setSelectedTopic] = useState<string>((queryTopic as string) ?? ALL_TOPICS_NAME);
    const items = [ALL_TOPICS_NAME, ...(selectedCampaign.topics ?? [])];
    const getTopicDisplayName = (topic: string): string =>
        topic === ALL_TOPICS_NAME ? translate("llm.topics-filter.all-topics") : topic;
    const displayedTopicName = getTopicDisplayName(selectedTopic);

    const onTopicSelection = (topic: string): void => {
        if (topic === selectedTopic) {
            return;
        }

        setSelectedTopic(topic);
        navigateToReport(selectedCampaign, queryModel as LLModel, currentTab.id, { topic });
        allTrackers.dispatchTrackEvent("Drop Down", "click", `Topic Change/${topic}`);
    };

    useEffect(() => {
        if (queryTopic && queryTopic !== selectedTopic) {
            setSelectedTopic(queryTopic as string);
        }
    }, [queryTopic]);

    return (
        <Dropdown
            data-automation="topics-filter-dropdown"
            appendTo="body"
            closeOnItemClick
            shouldScrollToSelected
            selectedIds={{ [selectedTopic]: true }}
            width={200}
            buttonWidth={180}
            onClick={({ id }) => onTopicSelection(id as string)}
            disabled={!selectedCampaign || isDisabled}
            dropdownPopupPlacement={"bottom-left"}
            cssClassContainer="DropdownContent-container FiltersBarDropdown llm-filter-dropdown"
        >
            {[
                <Box
                    data-automation="topics-filter-dropdown-button"
                    width="100%"
                >
                    <DropdownButton
                        key="llm-topic-dropdown-button"
                        cssClass="DropdownButton--filtersBarDropdownButton"
                        disabled={!selectedCampaign || isDisabled}
                        height={60}
                    >
                        <Flex
                            overflow="hidden"
                            alignItems="center"
                            margin="8px 0"
                            gap="12px"
                        >
                            <Topics
                                size="sm"
                                color={colorsPalettes.midnight[100]}
                                style={{ flexShrink: 0 }}
                            />
                            <TruncatedText title={displayedTopicName}>{displayedTopicName}</TruncatedText>
                        </Flex>
                    </DropdownButton>
                </Box>,
                ...items.map((topic) => {
                    const topicDisplayName = getTopicDisplayName(topic);

                    return (
                        <SimpleDropdownItem
                            key={topic}
                            id={topic}
                        >
                            <Box margin="8px 0">
                                <span title={topicDisplayName}>{topicDisplayName}</span>
                            </Box>
                        </SimpleDropdownItem>
                    );
                }),
            ]}
        </Dropdown>
    );
};
